import { useLocalStorage } from '@/hooks'
import { pruneAiChatSessionStorage } from '@/utils/aiChatStorage'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import {
  AgentStep,
  ChatAiComponent,
  ChatCatalogComponent,
  ChatCatalogRequest,
  ChatMessage,
  ChatResponse,
  ChatSession,
  getAiComponentsFromResponse,
  getCatalogComponentsFromResponse,
  getCatalogRequestsFromResponse,
  mergeAiComponents,
} from './types'

const CHAT_SESSION_STORAGE_KEY = 'ai-chat-session'

/**
 * Read session directly from localStorage
 */
function readSessionFromStorage(chatId: string): ChatSession | null {
  if (typeof window === 'undefined') return null
  const key = `${CHAT_SESSION_STORAGE_KEY}:${chatId}`
  const data = localStorage.getItem(key)
  if (!data) return null
  try {
    return JSON.parse(data) as ChatSession
  } catch {
    return null
  }
}

/**
 * Custom hook for managing chat session state with localStorage persistence.
 * Handles message streaming, accumulation, and agent steps.
 *
 * Uses refs for streaming content accumulation to avoid race conditions
 * when rapid websocket messages arrive faster than React can update state.
 */
export function useChatSession(chatId: string) {
  const [session, setSession, clearSession] = useLocalStorage<ChatSession | null>(
    `${CHAT_SESSION_STORAGE_KEY}:${chatId}`,
    null
  )

  // Use refs to accumulate streaming content without race conditions
  const streamingContentRef = useRef<string>('')
  const streamingThinkingRef = useRef<string>('')
  const streamingStepsRef = useRef<AgentStep[]>([])
  const streamingCatalogComponentsRef = useRef<ChatCatalogComponent[]>([])
  const streamingCatalogRequestsRef = useRef<ChatCatalogRequest[]>([])
  const streamingAiComponentsRef = useRef<ChatAiComponent[]>([])
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const previousChatIdRef = useRef<string>(chatId)

  // Explicitly sync session when chatId changes
  // This ensures we always load the correct data when switching chats
  useEffect(() => {
    if (previousChatIdRef.current !== chatId) {
      // ChatId changed - reset streaming refs and load from localStorage
      streamingContentRef.current = ''
      streamingThinkingRef.current = ''
      streamingStepsRef.current = []
      streamingCatalogComponentsRef.current = []
      streamingCatalogRequestsRef.current = []
      streamingAiComponentsRef.current = []

      // Clear any pending streaming updates
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current)
        updateTimeoutRef.current = null
      }

      // Read session from localStorage and update state
      const loadedSession = readSessionFromStorage(chatId)
      setSession(loadedSession)

      previousChatIdRef.current = chatId
    }
  }, [chatId, setSession])

  // Get or create session
  const getOrCreateSession = useCallback((): ChatSession => {
    if (session && session.chatId === chatId) {
      return session
    }
    return {
      chatId,
      messages: [],
      createdAt: Date.now(),
      lastUpdatedAt: Date.now(),
    }
  }, [session, chatId])

  // Get messages from session
  const messages = useMemo(() => session?.messages ?? [], [session])

  useEffect(() => {
    pruneAiChatSessionStorage({ keepChatId: chatId })
  }, [chatId, session?.lastUpdatedAt])

  // Flush accumulated streaming content to state
  const flushStreamingContent = useCallback(
    (isStreaming: boolean, cancelled = false) => {
      // Read directly from localStorage to get the latest state
      // This avoids stale closure issues with rapid updates
      const storageKey = `${CHAT_SESSION_STORAGE_KEY}:${chatId}`
      const storedData = localStorage.getItem(storageKey)

      if (!storedData) return

      let currentSession: ChatSession
      try {
        currentSession = JSON.parse(storedData)
      } catch {
        return
      }

      const sessionMessages = [...currentSession.messages]
      const lastMessage = sessionMessages[sessionMessages.length - 1]

      if (lastMessage && lastMessage.role === 'assistant') {
        sessionMessages[sessionMessages.length - 1] = {
          ...lastMessage,
          content: streamingContentRef.current,
          thinking: streamingThinkingRef.current,
          steps: [...streamingStepsRef.current],
          catalogComponents: [...streamingCatalogComponentsRef.current],
          catalogRequests: [...streamingCatalogRequestsRef.current],
          aiComponents: [...streamingAiComponentsRef.current],
          isStreaming,
          cancelled: cancelled || lastMessage.cancelled,
        }

        const updatedSession = {
          ...currentSession,
          messages: sessionMessages,
          lastUpdatedAt: Date.now(),
        }

        // Write directly to localStorage and update state
        localStorage.setItem(storageKey, JSON.stringify(updatedSession))
        setSession(updatedSession)
      }
    },
    [chatId, setSession]
  )

  // Add a user message and immediately start an assistant streaming message
  // This combines both operations to avoid race conditions with async state updates
  const addUserMessageAndStartStreaming = useCallback(
    (content: string): { userMessageId: string; assistantMessageId: string } => {
      const currentSession = getOrCreateSession()
      const userMessageId = crypto.randomUUID()
      const assistantMessageId = crypto.randomUUID()

      // Reset streaming refs
      streamingContentRef.current = ''
      streamingThinkingRef.current = ''
      streamingStepsRef.current = []
      streamingCatalogComponentsRef.current = []
      streamingCatalogRequestsRef.current = []
      streamingAiComponentsRef.current = []

      const userMessage: ChatMessage = {
        id: userMessageId,
        role: 'user',
        content,
        timestamp: Date.now(),
      }

      const assistantMessage: ChatMessage = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        thinking: '',
        isStreaming: true,
        steps: [],
        catalogComponents: [],
        catalogRequests: [],
        aiComponents: [],
        timestamp: Date.now(),
      }

      setSession({
        ...currentSession,
        messages: [...currentSession.messages, userMessage, assistantMessage],
        lastUpdatedAt: Date.now(),
      })

      return { userMessageId, assistantMessageId }
    },
    [getOrCreateSession, setSession]
  )

  // Add just a user message (for cases where we don't immediately expect a response)
  const addUserMessage = useCallback(
    (content: string): ChatMessage => {
      const currentSession = getOrCreateSession()
      const newMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'user',
        content,
        timestamp: Date.now(),
      }

      setSession({
        ...currentSession,
        messages: [...currentSession.messages, newMessage],
        lastUpdatedAt: Date.now(),
      })

      return newMessage
    },
    [getOrCreateSession, setSession]
  )

  // Start a new assistant message (streaming) - use addUserMessageAndStartStreaming instead when possible
  const startAssistantMessage = useCallback((): string => {
    const currentSession = getOrCreateSession()
    const messageId = crypto.randomUUID()

    // Reset streaming refs
    streamingContentRef.current = ''
    streamingThinkingRef.current = ''
    streamingStepsRef.current = []
    streamingCatalogComponentsRef.current = []
    streamingCatalogRequestsRef.current = []
    streamingAiComponentsRef.current = []

    const newMessage: ChatMessage = {
      id: messageId,
      role: 'assistant',
      content: '',
      thinking: '',
      isStreaming: true,
      steps: [],
      catalogComponents: [],
      catalogRequests: [],
      aiComponents: [],
      timestamp: Date.now(),
    }

    setSession({
      ...currentSession,
      messages: [...currentSession.messages, newMessage],
      lastUpdatedAt: Date.now(),
    })

    return messageId
  }, [getOrCreateSession, setSession])

  // Update the current streaming assistant message with a new chunk
  // Uses refs to accumulate content and debounces state updates
  const appendToAssistantMessage = useCallback(
    (response: ChatResponse) => {
      // Accumulate content in refs (no race conditions)
      if (response.response) {
        streamingContentRef.current += response.response
      }
      if (response.thinking) {
        streamingThinkingRef.current += response.thinking
      }

      const catalogComponents = getCatalogComponentsFromResponse(response)
      if (catalogComponents.length > 0) {
        streamingCatalogComponentsRef.current = catalogComponents
      }

      const catalogRequests = getCatalogRequestsFromResponse(response)
      if (catalogRequests.length > 0) {
        streamingCatalogRequestsRef.current = catalogRequests
      }

      const aiComponents = getAiComponentsFromResponse(response)
      if (aiComponents.length > 0) {
        streamingAiComponentsRef.current = mergeAiComponents(streamingAiComponentsRef.current, aiComponents)
      }

      // Handle agent steps
      const stepType = response.step_type ?? response.stepType
      const stepNumber = response.step_number ?? response.stepNumber
      const toolName = response.tool_name ?? response.toolName
      const toolArgs = response.tool_args ?? response.toolArgs
      const toolResult = response.tool_result ?? response.toolResult

      if (stepType && stepNumber !== null && stepNumber !== undefined) {
        const step: AgentStep = {
          stepNumber,
          stepType,
          toolName: toolName ?? undefined,
          toolArgs: toolArgs ?? undefined,
          toolResult: toolResult ?? undefined,
        }

        // Find existing step by BOTH stepNumber AND toolName to handle multiple tools
        // This ensures different tools with the same step number are kept separate
        const existingStepIndex = streamingStepsRef.current.findIndex(
          (s) => s.stepNumber === step.stepNumber && s.toolName === step.toolName
        )

        if (existingStepIndex >= 0) {
          // Update existing step (merge in new data like toolResult)
          streamingStepsRef.current[existingStepIndex] = {
            ...streamingStepsRef.current[existingStepIndex],
            ...step,
          }
        } else {
          // Add new step
          streamingStepsRef.current.push(step)
        }
      }

      // Debounce state updates to batch rapid messages
      // Clear any existing timeout
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current)
      }

      // Schedule a state update after a short delay
      // This batches rapid websocket messages into fewer state updates
      updateTimeoutRef.current = setTimeout(() => {
        flushStreamingContent(true)
        updateTimeoutRef.current = null
      }, 16) // ~60fps, fast enough for smooth UI but batches rapid updates
    },
    [flushStreamingContent]
  )

  // Finalize the current streaming message
  const finalizeAssistantMessage = useCallback(
    (_totalDuration?: number | null, cancelled = false) => {
      // Clear any pending debounced update
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current)
        updateTimeoutRef.current = null
      }

      // Final flush with isStreaming = false
      flushStreamingContent(false, cancelled)
    },
    [flushStreamingContent]
  )

  // Drop in-progress assistant output when backend signals an error.
  const discardStreamingAssistantMessage = useCallback(() => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current)
      updateTimeoutRef.current = null
    }

    streamingContentRef.current = ''
    streamingThinkingRef.current = ''
    streamingStepsRef.current = []
    streamingCatalogComponentsRef.current = []
    streamingCatalogRequestsRef.current = []
    streamingAiComponentsRef.current = []

    const currentSession = getOrCreateSession()
    const sessionMessages = [...currentSession.messages]
    const lastMessage = sessionMessages[sessionMessages.length - 1]

    if (lastMessage?.role === 'assistant' && lastMessage.isStreaming) {
      sessionMessages.pop()
      setSession({
        ...currentSession,
        messages: sessionMessages,
        lastUpdatedAt: Date.now(),
      })
    }
  }, [getOrCreateSession, setSession])

  const clearStreamingRefs = useCallback(() => {
    streamingContentRef.current = ''
    streamingThinkingRef.current = ''
    streamingStepsRef.current = []
    streamingCatalogComponentsRef.current = []
    streamingCatalogRequestsRef.current = []
    streamingAiComponentsRef.current = []
  }, [])

  // User-initiated cancel: keep partial output with a stopped footer, or drop an empty bubble.
  const cancelStreamingTurn = useCallback(
    (options?: { rollbackUserMessage?: boolean }) => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current)
        updateTimeoutRef.current = null
      }

      const hasOutput =
        streamingContentRef.current.length > 0 ||
        streamingThinkingRef.current.length > 0 ||
        streamingStepsRef.current.length > 0 ||
        streamingCatalogComponentsRef.current.length > 0 ||
        streamingCatalogRequestsRef.current.length > 0 ||
        streamingAiComponentsRef.current.length > 0

      if (hasOutput) {
        flushStreamingContent(false, true)
        clearStreamingRefs()
        return
      }

      discardStreamingAssistantMessage()

      // When the worker never ran, the user turn was never persisted server-side — roll it back locally too.
      if (!options?.rollbackUserMessage) return

      const currentSession = getOrCreateSession()
      const sessionMessages = [...currentSession.messages]
      const lastMessage = sessionMessages[sessionMessages.length - 1]

      if (lastMessage?.role === 'user') {
        sessionMessages.pop()
        setSession({
          ...currentSession,
          messages: sessionMessages,
          lastUpdatedAt: Date.now(),
        })
      }
    },
    [clearStreamingRefs, discardStreamingAssistantMessage, flushStreamingContent, getOrCreateSession, setSession]
  )

  // Clear all messages in the session
  const clearMessages = useCallback(() => {
    // Reset streaming refs
    streamingContentRef.current = ''
    streamingThinkingRef.current = ''
    streamingStepsRef.current = []
    streamingCatalogComponentsRef.current = []
    streamingCatalogRequestsRef.current = []
    streamingAiComponentsRef.current = []

    if (session) {
      setSession({
        ...session,
        messages: [],
        lastUpdatedAt: Date.now(),
      })
    }
  }, [session, setSession])

  // Load messages from an existing session
  const loadFromSession = useCallback(
    (loadedSession: ChatSession) => {
      // Reset streaming refs when loading
      streamingContentRef.current = ''
      streamingThinkingRef.current = ''
      streamingStepsRef.current = []
      streamingCatalogComponentsRef.current = []
      streamingCatalogRequestsRef.current = []
      streamingAiComponentsRef.current = []

      setSession(loadedSession)
    },
    [setSession]
  )

  // Check if there's currently a streaming message
  const isStreaming = useMemo(() => {
    const lastMessage = messages[messages.length - 1]
    return lastMessage?.role === 'assistant' && lastMessage.isStreaming === true
  }, [messages])

  return {
    session,
    messages,
    isStreaming,
    addUserMessage,
    addUserMessageAndStartStreaming,
    startAssistantMessage,
    appendToAssistantMessage,
    finalizeAssistantMessage,
    cancelStreamingTurn,
    discardStreamingAssistantMessage,
    clearMessages,
    clearSession,
    loadFromSession,
  }
}
